import { useCallback, useEffect, useRef, useState } from 'react';
import { trackAmpAssetDeleted, trackAmpAssetUploaded, assetSizeBucket } from '@/services/amplitudeService';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { useGenderLabel } from '@/v2/utils/gender';
import {
  Mic,
  Wand2,
  Camera,
  Video,
  BookMarked,
  Upload,
  AudioLines,
  X,
  Play,
  Square,
  Loader2,
  Search,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  FolderOpen,
  FileText,
  Pencil,
  Trash2,
} from 'lucide-react';
import { cn, assetFileError, SUPPORTED_ASSET_TYPES } from '@/v2/utils';
import { v2Service } from '@/api/v2Service';
import type {
  V2OwnedVoice, V2Voice, V2OwnedAvatar, V2AvatarGroup, V2AvatarLook,
  V2AvatarCharacter, V2CharacterLook, V2Brand, V2Asset,
} from '@/api/v2Types';
import { useUserStore } from '@/store/userStore';
import { seedHomeAgent } from '@/v2/seed';
import { AiAvatarModal, PhotoAvatarModal, DigitalTwinModal } from '@/v2/components/AvatarCreateModals';
import { CloneVoiceModal, DesignVoiceModal } from '@/v2/components/VoiceCreateModals';
import { BrandKitModal } from '@/v2/components/BrandKitModal';
import { resolveOwnedAvatarMedia } from '@/v2/avatarPreview';
import { togglePreview, stopPreview, isPreviewPlaying, isPreviewLoading, onPreviewChange } from '@/v2/audio';
import { vConfirm, vPrompt } from '@/v2/dialogs';
import { toast } from '@/v2/toast';

// Friendly, non-leaky status copy for an async-trained asset (avatar/voice).
// Raw backend statuses ("processing", "completed") never reach the user.
function assetStatusInfo(status?: string, needsConsent?: boolean): { label: string; tone: string; pending: boolean } {
  if (needsConsent) return { label: i18n.t('assets.statusConsentNeeded', { ns: 'v2' }), tone: 'bg-amber-500/85 text-black', pending: false };
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'ready') return { label: i18n.t('assets.statusReady', { ns: 'v2' }), tone: 'bg-primary/85 text-white', pending: false };
  if (s === 'failed' || s === 'error') return { label: i18n.t('assets.statusFailed', { ns: 'v2' }), tone: 'bg-red-500/85 text-white', pending: false };
  return { label: i18n.t('assets.statusCreating', { ns: 'v2' }), tone: 'bg-black/65 text-white', pending: true };
}

type TabId = 'all' | 'avatars' | 'voices' | 'styles' | 'files';

const tabs: { id: TabId; labelKey: string }[] = [
  { id: 'all', labelKey: 'assets.tabAll' },
  { id: 'avatars', labelKey: 'assets.tabAvatars' },
  { id: 'voices', labelKey: 'assets.tabVoices' },
  { id: 'styles', labelKey: 'assets.tabBrandKits' },
  { id: 'files', labelKey: 'assets.tabFiles' },
];

const NOT_DEPLOYED = () => i18n.t('assets.notDeployed', { ns: 'v2' });

function AddNewPills({
  options,
}: {
  options: { icon: typeof Mic; label: string; onClick?: () => void; comingSoon?: boolean }[];
}) {
  const { t } = useTranslation('v2');
  // Columns follow the option count so the row fills evenly (e.g. 3 avatar
  // modes sit 1×3 instead of an awkward 2+1).
  const cols = options.length >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2';
  return (
    <div className={cn('grid grid-cols-1 gap-4 mb-8', cols)}>
      {options.map((option) => (
        <button
          key={option.label}
          onClick={option.onClick}
          className={cn(
            'group flex items-center gap-3 px-5 py-4 rounded-full bg-surface border border-glass-border transition-all duration-200 text-left',
            option.comingSoon
              ? 'opacity-70'
              : 'hover:bg-surface-light hover:border-primary/40 hover:shadow-glow-sm hover:-translate-y-0.5'
          )}
        >
          <span className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 group-hover:from-primary/30 group-hover:to-accent/30 flex items-center justify-center shrink-0 transition-colors">
            <option.icon className="w-5 h-5 text-text-primary group-hover:text-primary transition-colors" />
          </span>
          <span className="font-semibold text-text-primary truncate">{option.label}</span>
          {option.comingSoon && (
            <span className="ml-auto shrink-0 px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-bold uppercase tracking-wide">{t('assets.soon')}</span>
          )}
        </button>
      ))}
    </div>
  );
}

function Note({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p className="text-xs text-amber-300/80 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-4">
      {text}
    </p>
  );
}

// Portaled — `.glass` ancestors (backdrop-filter) trap fixed overlays otherwise.
function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in" onClick={onClose}>
      <div
        className={cn('w-full rounded-2xl bg-surface border border-glass-border p-5 shadow-2xl animate-slide-up max-h-[85vh] overflow-y-auto', wide ? 'max-w-2xl' : 'max-w-md')}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-text-primary">{title}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface-light flex items-center justify-center text-text-secondary">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

const inputCls =
  'w-full bg-surface-light border border-glass-border rounded-xl px-4 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:ring-1 focus:ring-primary/50 focus:border-primary';

// A lazy image that shows a pulsing skeleton until it loads, then fades in — so stock
// tiles never sit as flat, empty rectangles while you scroll. The parent must be
// `relative` (the image + skeleton fill it absolutely).
function LazyImg({ src, alt, imgClassName }: { src?: string; alt: string; imgClassName?: string }) {
  const [loaded, setLoaded] = useState(false);
  if (!src) return null;
  return (
    <>
      {!loaded && <span className="absolute inset-0 bg-surface-light animate-pulse" />}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        className={cn('absolute inset-0 w-full h-full object-cover transition-[opacity,transform] duration-300', loaded ? 'opacity-100' : 'opacity-0', imgClassName)}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Avatars — owned (status + consent) and full stock library (looks + paging)
// ---------------------------------------------------------------------------

function OwnedAvatarCard({ a, onChanged }: { a: V2OwnedAvatar; onChanged: () => void }) {
  const { t } = useTranslation('v2');
  // How this avatar was created — localized (was a raw English "prompt"/"photo").
  const kindLabel =
    a.kind === 'prompt' ? t('assets.avatarKindPrompt')
    : a.kind === 'photo' ? t('assets.avatarKindPhoto')
    : a.kind === 'digital_twin' ? t('assets.avatarKindDigitalTwin')
    : a.kind;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<string | undefined>(a.preview_image_url);
  const [avatarId, setAvatarId] = useState<string | undefined>(a.avatar_id);
  const [status, setStatus] = useState(a.status);
  const [consentStatus, setConsentStatus] = useState<string | null | undefined>(undefined);

  // Self-heal: rows created pre-redeploy have no stored preview — fetch it
  // once from the avatar's looks (cached per ref for the session).
  useEffect(() => {
    if (preview || !userId) return;
    let on = true;
    void resolveOwnedAvatarMedia(userId, a.avatar_ref).then((m) => {
      if (!on) return;
      if (m.img) setPreview((v) => v || m.img);
      if (m.lookId) setAvatarId((v) => v || m.lookId);
      if (m.status) setStatus((v) => m.status || v);
    });
    return () => { on = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, a.avatar_ref]);

  const refresh = async () => {
    if (!userId || busy) return;
    setBusy(true);
    try {
      const st = await v2Service.avatarStatus(userId, a.avatar_ref);
      if (st.preview_image_url) setPreview(st.preview_image_url);
      if (st.avatar_id) setAvatarId(st.avatar_id);
      if (st.status) setStatus(st.status);
      setConsentStatus(st.consent_status);
      onChanged();
    } catch { /* pre-deploy */ } finally { setBusy(false); }
  };

  // Auto-poll while training so the user never has to hit refresh — the card
  // updates itself and toasts the moment the avatar is ready or needs consent.
  const settled = status === 'completed' || status === 'ready' || status === 'failed'
    || consentStatus === 'pending';
  useEffect(() => {
    if (!userId || settled) return;
    let on = true;
    let polls = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = async () => {
      // Skip the HeyGen-backed status call while the tab is backgrounded; keep the loop alive
      // (re-check shortly) so it resumes the moment the user comes back. No visible change.
      if (document.hidden) { timer = setTimeout(() => void tick(), 3000); return; }
      try {
        const st = await v2Service.avatarStatus(userId, a.avatar_ref);
        if (!on) return;
        if (st.preview_image_url) setPreview((v) => v || st.preview_image_url);
        if (st.avatar_id) setAvatarId((v) => v || st.avatar_id);
        if (st.consent_status !== undefined) setConsentStatus(st.consent_status);
        if (st.status && st.status !== status) {
          setStatus(st.status);
          if (st.status === 'completed' || st.status === 'ready') {
            toast.success(t('assets.avatarReadyToast', { name: a.name }));
            onChanged();
          } else if (st.status === 'failed') {
            toast.error(t('assets.avatarFailedToast', { name: a.name }));
            onChanged();
          }
        }
        if (st.consent_status === 'pending') toast.info(t('assets.avatarConsentToast', { name: a.name }));
      } catch { /* keep trying */ }
      if (!on) return;
      // Poll frequently while HeyGen works (it can be slow), then ease off.
      polls += 1;
      timer = setTimeout(() => void tick(), polls < 40 ? 3000 : 7000);
    };
    timer = setTimeout(() => void tick(), 2500);  // first check soon after creation
    return () => { on = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, a.avatar_ref, settled, status]);

  const consent = async () => {
    if (!userId || busy) return;
    setBusy(true);
    try {
      const res = await v2Service.createAvatarConsent(userId, a.avatar_ref, window.location.href);
      if (res.consent_url) {
        window.open(res.consent_url, '_blank', 'noopener');
        toast.info(t('assets.consentOpened'));
      } else {
        toast.error(t('assets.consentNotStarted'));
      }
    } catch {
      toast.error(t('assets.consentNotAvailable'));
    } finally { setBusy(false); }
  };

  const ready = status === 'completed' || status === 'ready';
  const needsConsent = status === 'pending_consent' || consentStatus === 'pending';
  const statusInfo = assetStatusInfo(status, needsConsent);
  const useInVideo = () => {
    if (!avatarId) return;
    seedHomeAgent({ avatarId, avatarName: a.name, avatarImg: preview });
    navigate('/');
  };

  const rename = async () => {
    if (!userId || busy) return;
    const name = await vPrompt(t('assets.renameAvatarPrompt'), { defaultValue: a.name });
    if (!name?.trim() || name.trim() === a.name) return;
    setBusy(true);
    try {
      await v2Service.renameAvatar(userId, a.avatar_ref, name.trim());
      onChanged();
      toast.success(t('assets.avatarRenamed'));
    } catch { toast.error(t('assets.avatarRenameFailed')); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!userId || busy) return;
    if (!await vConfirm(t('assets.deleteAvatarConfirm', { name: a.name }))) return;
    setBusy(true);
    try {
      await v2Service.deleteAvatar(userId, a.avatar_ref);
      trackAmpAssetDeleted({ assetType: 'avatar' });
      onChanged();
      toast.success(t('assets.itemDeleted', { name: a.name }));
    } catch { toast.error(t('assets.deleteFailed')); }
    finally { setBusy(false); }
  };

  return (
    <div className="group">
      <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-gradient-to-br from-primary/40 to-accent/40 group-hover:ring-2 group-hover:ring-primary/40 transition-all">
        {preview && <LazyImg src={preview} alt={a.name} />}
        {!preview && statusInfo.pending && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/85">
            <Loader2 className="w-6 h-6 animate-spin" />
            <span className="text-[11px] font-medium">{t('assets.creatingYourAvatar')}</span>
          </div>
        )}
        <span className={cn(
          'absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold',
          statusInfo.tone
        )}>
          {statusInfo.pending && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
          {statusInfo.label}
        </span>
        {!ready && (
          <button
            onClick={() => void refresh()}
            title={t('assets.checkNow')}
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center text-white/80 hover:text-white"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', busy && 'animate-spin')} />
          </button>
        )}
        <div className="absolute inset-x-2 bottom-2 flex gap-1.5">
          {needsConsent && (
            <button onClick={() => void consent()} className="flex-1 py-1.5 rounded-lg bg-amber-400 text-black text-[11px] font-bold flex items-center justify-center gap-1">
              <ShieldCheck className="w-3 h-3" /> {t('assets.consent')}
            </button>
          )}
          {ready && (
            <button onClick={useInVideo} className="flex-1 py-1.5 rounded-lg bg-gradient-to-r from-primary to-accent text-white text-[11px] font-bold flex items-center justify-center gap-1">
              <Sparkles className="w-3 h-3" /> {t('assets.useInVideo')}
            </button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-medium text-text-primary truncate">{a.name}</p>
          <p className="text-xs text-text-tertiary">{kindLabel}</p>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
          <button
            onClick={() => void rename()}
            title={t('assets.rename')}
            className="w-7 h-7 rounded-full hover:bg-surface-light flex items-center justify-center text-text-tertiary hover:text-text-primary"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => void remove()}
            title={t('assets.deleteAvatar')}
            className="w-7 h-7 rounded-full hover:bg-surface-light flex items-center justify-center text-text-tertiary hover:text-red-400"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AvatarsSection() {
  const { t } = useTranslation('v2');
  const genderLabel = useGenderLabel();
  const userId = useUserStore((s) => s.userId);
  const navigate = useNavigate();
  const [owned, setOwned] = useState<V2OwnedAvatar[]>([]);
  const [chars, setChars] = useState<V2AvatarCharacter[]>([]);
  const [charFor, setCharFor] = useState<V2AvatarCharacter | null>(null);
  // Legacy (oldest deployed backend): groups + token paging, drill for looks.
  const [legacyGroups, setLegacyGroups] = useState<V2AvatarGroup[] | null>(null);
  const [legacyToken, setLegacyToken] = useState<string | null>(null);
  const [looksFor, setLooksFor] = useState<V2AvatarGroup | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  // starts true → skeletons render from the very first frame (no empty flash)
  const [loadingMore, setLoadingMore] = useState(true);
  const [search, setSearch] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [modal, setModal] = useState<'ai' | 'photo' | 'twin' | null>(null);

  const load = useCallback(async (off: number, term: string, token?: string | null) => {
    setLoadingMore(true);
    try {
      const res = await v2Service.listAvatars({ userId: userId ?? undefined, offset: off, search: term || undefined, limit: 24, token });
      if (res.legacy_groups) {
        setLegacyGroups((prev) => (token && prev ? [...prev, ...res.legacy_groups!] : res.legacy_groups!));
        setLegacyToken(res.has_more ? res.legacy_next_token ?? null : null);
        setChars([]); setHasMore(false);
      } else {
        const incoming = res.characters || [];
        setChars((prev) => {
          if (!off) return incoming;
          // merge across pages by character name (looks may span page edges)
          const map = new Map(prev.map((c) => [c.name, { ...c, looks: [...c.looks] }]));
          for (const c of incoming) {
            const hit = map.get(c.name);
            if (!hit) { map.set(c.name, c); continue; }
            const seen = new Set(hit.looks.map((l) => l.avatar_id));
            for (const lk of c.looks) if (!seen.has(lk.avatar_id)) hit.looks.push(lk);
            hit.looks_count = hit.looks.length;
          }
          return [...map.values()];
        });
        setHasMore(!!res.has_more);
        setOffset(res.next_offset ?? off);
        setLegacyGroups(null);
      }
      if (!off && !token) setOwned(res.owned || []);
      setNote(null);
    } catch {
      setNote(NOT_DEPLOYED());
    } finally { setLoadingMore(false); }
  }, [userId]);

  // immediate on mount, debounced while typing a search
  useEffect(() => { const t = setTimeout(() => void load(0, search), search ? 300 : 0); return () => clearTimeout(t); }, [load, search]);

  const useCharacter = (c: V2AvatarCharacter) => {
    if (c.looks.length === 1) {
      const lk = c.looks[0];
      seedHomeAgent({ avatarId: lk.avatar_id, avatarName: c.name, avatarImg: lk.preview_image_url });
      navigate('/');
      return;
    }
    setCharFor(c); // multi-look → pick a look first
  };

  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-text-primary mb-5">{t('assets.avatars')}</h2>
      <AddNewPills
        options={[
          { icon: Wand2, label: t('assets.createWithAi'), onClick: () => setModal('ai') },
          { icon: Camera, label: t('assets.photoToAvatar'), onClick: () => setModal('photo') },
          // Digital twin temporarily disabled (training/consent flow not reliable yet).
          { icon: Video, label: t('assets.digitalTwin'), comingSoon: true, onClick: () => toast.info(t('assets.digitalTwinComingSoon')) },
        ]}
      />
      <Note text={note} />

      {owned.length > 0 && (
        <>
          <p className="text-sm font-semibold text-text-secondary mb-3">{t('assets.yours')}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            {owned.map((a) => <OwnedAvatarCard key={a.avatar_ref} a={a} onChanged={() => void load(0, search)} />)}
          </div>
        </>
      )}

      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-semibold text-text-secondary">{t('assets.stockLibrary')}</p>
        <div className="relative w-56 max-w-[55%]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('assets.searchAvatars')}
            className="w-full bg-surface-light border border-glass-border rounded-full pl-9 pr-3 py-1.5 text-xs text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {loadingMore && !chars.length && !legacyGroups?.length
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i}>
                <div className="aspect-[3/4] rounded-2xl bg-surface-light animate-pulse mb-2" />
                <div className="h-3.5 w-2/3 rounded bg-surface-light animate-pulse" />
                <div className="h-2.5 w-1/3 rounded bg-surface-light animate-pulse mt-1.5" />
              </div>
            ))
          : legacyGroups
          ? legacyGroups
              .filter((g) => !search.trim() || g.name.toLowerCase().includes(search.trim().toLowerCase()))
              .map((g) => (
                <button key={g.group_id} onClick={() => setLooksFor(g)} className="group text-left">
                  <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-surface-light group-hover:ring-2 group-hover:ring-primary/40 group-hover:shadow-glow-sm transition-all">
                    {g.preview_image_url
                      ? <LazyImg src={g.preview_image_url} alt={g.name} imgClassName="group-hover:scale-[1.03]" />
                      : <div className="w-full h-full grid place-items-center text-text-tertiary text-xs">{g.name}</div>}
                    <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pt-8 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <span className="text-[11px] font-semibold text-white">{t('assets.browseLooks')}</span>
                    </span>
                  </div>
                  <p className="text-sm font-medium text-text-primary truncate">{g.name}</p>
                  <p className="text-xs text-text-tertiary">{t('assets.looksCount', { num: g.looks_count ?? '—' })}</p>
                </button>
              ))
          : chars.map((c) => (
              <button key={c.name} onClick={() => useCharacter(c)} className="group text-left">
                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-surface-light group-hover:ring-2 group-hover:ring-primary/40 group-hover:shadow-glow-sm transition-all">
                  {c.preview_image_url
                    ? <LazyImg src={c.preview_image_url} alt={c.name} imgClassName="group-hover:scale-[1.03]" />
                    : <div className="w-full h-full grid place-items-center text-text-tertiary text-xs">{c.name}</div>}
                  {c.looks_count > 1 && (
                    <span className="absolute top-2 right-2 px-1.5 py-0.5 rounded-md bg-black/60 text-[10px] font-bold text-white">{t('assets.looksBadge', { count: c.looks_count })}</span>
                  )}
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 pt-8 pb-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="text-[11px] font-semibold text-white">{c.looks_count > 1 ? t('assets.chooseALookArrow') : t('assets.useInVideoArrow')}</span>
                  </span>
                </div>
                <p className="text-sm font-medium text-text-primary truncate">{c.name}</p>
                <p className="text-xs text-text-tertiary capitalize">{genderLabel(c.gender)}</p>
              </button>
            ))}
      </div>
      {!chars.length && !legacyGroups?.length && !loadingMore && !note && (
        <p className="text-sm text-text-tertiary">{t('assets.noAvatarsMatch', { search })}</p>
      )}
      {(hasMore || legacyToken) && (
        <button
          onClick={() => void load(offset, search, legacyToken)}
          disabled={loadingMore}
          className="mt-5 px-5 py-2.5 rounded-full glass text-sm text-text-secondary hover:text-text-primary transition-colors disabled:opacity-50"
        >
          {loadingMore ? t('assets.loading') : t('assets.loadMoreAvatars')}
        </button>
      )}

      {modal === 'ai' && <AiAvatarModal onClose={() => setModal(null)} onCreated={() => void load(0, search)} />}
      {modal === 'photo' && <PhotoAvatarModal onClose={() => setModal(null)} onCreated={() => void load(0, search)} />}
      {modal === 'twin' && <DigitalTwinModal onClose={() => setModal(null)} onCreated={() => void load(0, search)} />}
      {looksFor && <LooksModal group={looksFor} onClose={() => setLooksFor(null)} />}
      {charFor && <CharacterLooksModal character={charFor} onClose={() => setCharFor(null)} />}
    </section>
  );
}

/** Legacy-backend drill: a group's looks (pick one to use in a video).
 *  HeyGen's looks endpoint is broken for ~60% of stock groups — failures show
 *  a friendly note instead of crashing. Unnecessary after the backend redeploy. */
function LooksModal({ group, onClose }: { group: V2AvatarGroup; onClose: () => void }) {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const [looks, setLooks] = useState<V2AvatarLook[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const res = await v2Service.listAvatarLooks(group.group_id);
        if (!on) return;
        const lk = res.looks || [];
        setLooks(lk);
        setFailed(lk.length === 0);
      } catch {
        if (!on) return;
        setLooks([]);
        setFailed(true);
      }
    })();
    return () => { on = false; };
  }, [group.group_id]);

  const pick = (lk: V2AvatarLook) => {
    seedHomeAgent({ avatarId: lk.avatar_id, avatarName: `${group.name} — ${lk.name}`, avatarImg: lk.preview_image_url });
    onClose();
    navigate('/');
  };

  return (
    <Modal title={t('assets.looksModalTitle', { name: group.name })} onClose={onClose} wide>
      {looks === null && <p className="text-sm text-text-tertiary flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {t('assets.loadingLooks')}</p>}
      {failed && (
        <p className="text-sm text-text-tertiary">
          {t('assets.avatarPackUnavailable')}
        </p>
      )}
      {!!looks?.length && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {looks.map((lk) => (
            <button key={lk.avatar_id} onClick={() => pick(lk)} className="group text-left">
              <div className="aspect-[3/4] rounded-xl overflow-hidden bg-surface-light mb-1.5 group-hover:ring-2 group-hover:ring-primary/50 transition-all">
                {lk.preview_image_url && <img src={lk.preview_image_url} alt={lk.name} loading="lazy" className="w-full h-full object-cover" />}
              </div>
              <p className="text-xs font-medium text-text-primary truncate">{lk.name}</p>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/** Character look chooser — looks come bundled with the character, so this is
 *  instant (no API call). Picking a look seeds it into the Home agent. */
function CharacterLooksModal({ character, onClose }: { character: V2AvatarCharacter; onClose: () => void }) {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();

  const pick = (lk: V2CharacterLook) => {
    seedHomeAgent({ avatarId: lk.avatar_id, avatarName: `${character.name} — ${lk.label}`, avatarImg: lk.preview_image_url });
    onClose();
    navigate('/');
  };

  return (
    <Modal title={t('assets.chooseLookModalTitle', { name: character.name })} onClose={onClose} wide>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {character.looks.map((lk) => (
          <button key={lk.avatar_id} onClick={() => pick(lk)} className="group text-left">
            <div className="aspect-[3/4] rounded-xl overflow-hidden bg-surface-light mb-1.5 group-hover:ring-2 group-hover:ring-primary/50 transition-all">
              {lk.preview_image_url && <img src={lk.preview_image_url} alt={lk.label} loading="lazy" className="w-full h-full object-cover" />}
            </div>
            <p className="text-xs font-medium text-text-primary truncate">{lk.label}</p>
          </button>
        ))}
      </div>
      <p className="text-[10px] text-text-tertiary mt-3">{t('assets.pickLookHint')}</p>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Voices — search, owned status refresh, use-in-video
// ---------------------------------------------------------------------------

function VoiceRow({
  name, sub, previewUrl, tag, pending, onUse, onRefresh, refreshing,
}: {
  name: string; sub?: string; previewUrl?: string; tag?: string; pending?: boolean;
  onUse?: () => void; onRefresh?: () => void; refreshing?: boolean;
}) {
  const { t } = useTranslation('v2');
  // Reflect the shared player's live state so the button TOGGLES: Play → (buffering)
  // spinner → Stop while playing → back to Play on end/stop — same as the home / plan-card
  // selector windows. (The old button only ever restarted, so a preview couldn't be paused.)
  const [, force] = useState(0);
  useEffect(() => onPreviewChange(() => force((n) => n + 1)), []);
  const playing = isPreviewPlaying(previewUrl);
  const loading = isPreviewLoading(previewUrl);
  return (
    <div className="flex items-center gap-4 p-4 rounded-2xl glass hover:bg-surface-light transition-colors">
      <button
        onClick={() => { if (previewUrl) togglePreview(previewUrl); }}
        disabled={pending || !previewUrl}
        className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/40 to-accent/40 flex items-center justify-center shrink-0 hover:scale-105 transition-transform disabled:hover:scale-100"
        title={pending ? t('assets.stillTraining') : !previewUrl ? t('assets.noPreview') : (playing || loading ? t('assets.stop') : t('assets.playPreview'))}
      >
        {(pending || loading) ? <Loader2 className="w-4 h-4 text-text-primary animate-spin" />
          : playing ? <Square className="w-4 h-4 text-text-primary fill-current" />
            : previewUrl ? <Play className="w-4 h-4 text-text-primary" /> : <AudioLines className="w-4 h-4 text-text-primary" />}
      </button>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-text-primary truncate">
          {name} {tag && <span className="ml-1 px-1.5 py-0.5 rounded bg-primary/20 text-[10px] text-primary align-middle">{tag}</span>}
        </p>
        {sub && <p className="text-xs text-text-tertiary truncate">{sub}</p>}
      </div>
      {onRefresh && (
        <button onClick={onRefresh} title={t('assets.refreshStatus')} className="w-8 h-8 rounded-full bg-surface flex items-center justify-center text-text-tertiary hover:text-text-primary">
          <RefreshCw className={cn('w-3.5 h-3.5', refreshing && 'animate-spin')} />
        </button>
      )}
      {onUse && (
        <button onClick={onUse} className="px-3 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-xs font-semibold text-primary whitespace-nowrap">
          {t('assets.use')}
        </button>
      )}
    </div>
  );
}

function VoicesSection() {
  const { t } = useTranslation('v2');
  const genderLabel = useGenderLabel();
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const [owned, setOwned] = useState<V2OwnedVoice[]>([]);
  const [stock, setStock] = useState<V2Voice[]>([]);
  const [search, setSearch] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [priming, setPriming] = useState(false);
  const [modal, setModal] = useState<'clone' | 'design' | null>(null);
  const [refreshingRef, setRefreshingRef] = useState<string | null>(null);
  const primeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track per-voice status across loads so we can toast pending → ready exactly
  // once (seeded only after the first load, so existing voices don't toast).
  const prevStatusRef = useRef<Record<string, string> | null>(null);

  const load = useCallback(async (q?: string, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await v2Service.listVoices({ userId: userId ?? undefined, limit: 24, search: q || undefined });
      const newOwned = res.owned || [];
      if (prevStatusRef.current) {
        for (const v of newOwned) {
          const prev = prevStatusRef.current[v.voice_ref];
          if (prev && prev !== 'ready' && v.status === 'ready') {
            toast.success(t('assets.voiceReadyToast', { name: v.name }));
          }
        }
      }
      prevStatusRef.current = Object.fromEntries(newOwned.map((v) => [v.voice_ref, v.status || '']));
      setOwned(newOwned);
      setStock(res.voices || []);
      setNote(null);
      // Backend is warming the full catalogue (cold cache) → re-fetch shortly
      // so the partial first-page result fills in without a manual reload.
      setPriming(!!res.cache_priming);
      if (res.cache_priming) {
        if (primeTimer.current) clearTimeout(primeTimer.current);
        primeTimer.current = setTimeout(() => void load(q), 4000);
      }
    } catch {
      setNote(NOT_DEPLOYED());
    } finally {
      if (!silent) setLoading(false);
    }
  }, [userId]);

  useEffect(() => () => { stopPreview(); if (primeTimer.current) clearTimeout(primeTimer.current); }, []);

  // Single load path: fire immediately on mount, debounce on search change.
  useEffect(() => {
    const t = setTimeout(() => void load(search), search ? 350 : 0);
    return () => clearTimeout(t);
  }, [search, load]);

  const refreshVoice = async (v: V2OwnedVoice) => {
    if (!userId) return;
    setRefreshingRef(v.voice_ref);
    try { await v2Service.voiceStatus(userId, v.voice_ref); await load(search); }
    catch { /* pre-deploy */ } finally { setRefreshingRef(null); }
  };

  // Auto-poll while any cloned/designed voice is still training, so it flips to
  // ready (and toasts) without the user hitting refresh. Silent: no skeletons.
  const hasPendingVoice = owned.some((v) => v.status && v.status !== 'ready' && v.status !== 'failed');
  useEffect(() => {
    if (!userId || !hasPendingVoice) return;
    // Don't re-pull the voice list while the tab is backgrounded (resumes when visible).
    const id = setInterval(() => { if (!document.hidden) void load(search, true); }, 9000);
    return () => clearInterval(id);
  }, [userId, hasPendingVoice, load, search]);

  const useVoice = (id: string, name: string) => {
    seedHomeAgent({ voiceId: id, voiceName: name });
    navigate('/');
  };

  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-text-primary mb-5">{t('assets.voices')}</h2>
      <AddNewPills
        options={[
          { icon: Mic, label: t('assets.cloneYourVoice'), onClick: () => setModal('clone') },
          { icon: Wand2, label: t('assets.designAVoice'), onClick: () => setModal('design') },
        ]}
      />
      <Note text={note} />

      {/* Real search across the public catalogue (2,329 voices) */}
      <div className="relative mb-4">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-tertiary" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('assets.searchVoices')}
          className={cn(inputCls, 'pl-11')}
        />
      </div>

      {/* My voices — cloned/designed, clearly separated from the public library */}
      <p className="text-sm font-semibold text-text-secondary mb-3">{t('assets.myVoices')} {owned.length ? `(${owned.length})` : ''}</p>
      {owned.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {owned.map((v) => {
            const pending = !!v.status && v.status !== 'ready' && v.status !== 'failed';
            const friendly = v.status === 'failed' ? t('assets.statusFailed') : pending ? t('assets.statusCreating') : null;
            return (
              <VoiceRow
                key={v.voice_ref}
                name={v.name}
                sub={[v.kind === 'clone' ? t('assets.voiceClone') : t('assets.voiceDesign'), v.language, genderLabel(v.gender), friendly].filter(Boolean).join(' · ')}
                previewUrl={v.preview_audio_url}
                tag={t('assets.yours')}
                pending={pending}
                onUse={v.status === 'ready' ? () => useVoice(v.voice_id, v.name) : undefined}
                onRefresh={pending ? () => void refreshVoice(v) : undefined}
                refreshing={refreshingRef === v.voice_ref}
              />
            );
          })}
        </div>
      ) : loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 p-4 rounded-2xl glass animate-pulse">
              <div className="w-10 h-10 rounded-full bg-surface-light shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/2 rounded bg-surface-light" />
                <div className="h-2.5 w-1/3 rounded bg-surface-light" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs text-text-tertiary mb-8">{t('assets.noVoicesYet')}</p>
      )}

      <p className="text-sm font-semibold text-text-secondary mb-3 flex items-center gap-2">
        {t('assets.publicLibrary')}
        {priming && <span className="text-[11px] font-normal text-text-tertiary flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> {t('assets.loadingMore')}</span>}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {loading && !stock.length
          ? Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-2xl glass animate-pulse">
                <div className="w-10 h-10 rounded-full bg-surface-light shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/2 rounded bg-surface-light" />
                  <div className="h-2.5 w-1/3 rounded bg-surface-light" />
                </div>
              </div>
            ))
          : stock.length
            ? stock.map((v) => (
                <VoiceRow
                  key={v.voice_id}
                  name={v.name}
                  sub={[v.language, genderLabel(v.gender)].filter(Boolean).join(' · ')}
                  previewUrl={v.preview_audio_url}
                  onUse={() => useVoice(v.voice_id, v.name)}
                />
              ))
            : !note && <p className="text-xs text-text-tertiary">{t('assets.noVoicesMatch', { search })}</p>}
      </div>

      {modal === 'clone' && <CloneVoiceModal onClose={() => setModal(null)} onDone={() => void load(search)} />}
      {modal === 'design' && <DesignVoiceModal onClose={() => setModal(null)} onDone={() => void load(search)} />}
    </section>
  );
}


// ---------------------------------------------------------------------------
// Styles — full real gallery with tag filter; pick → seed Home agent
// ---------------------------------------------------------------------------

function BrandsSection() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const [brands, setBrands] = useState<V2Brand[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<{ open: boolean; existing?: V2Brand }>({ open: false });

  const load = useCallback(async () => {
    if (!userId) { setLoading(false); return; }
    try {
      const res = await v2Service.listBrands(userId);
      setBrands(res.brands || []);
      setNote(null);
    } catch {
      setNote(NOT_DEPLOYED());
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  const useBrand = (b: V2Brand) => {
    seedHomeAgent({ brandRef: b.brand_ref, brandName: b.name, brandLogo: b.logo_url });
    navigate('/');
  };

  const removeBrand = async (b: V2Brand) => {
    if (!userId) return;
    if (!await vConfirm(t('assets.deleteBrandConfirm', { name: b.name }))) return;
    try { await v2Service.deleteBrand(userId, b.brand_ref); trackAmpAssetDeleted({ assetType: 'brand' }); await load(); toast.success(t('assets.itemDeleted', { name: b.name })); }
    catch { toast.error(t('assets.deleteFailed')); }
  };

  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-text-primary mb-5">{t('assets.brandKits')}</h2>
      <AddNewPills
        options={[
          { icon: BookMarked, label: t('assets.createABrandKit'), onClick: () => setModal({ open: true }) },
        ]}
      />
      <Note text={note} />

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="rounded-2xl border border-glass-border bg-surface-light/40 p-4 animate-pulse">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-surface-light shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 w-2/3 rounded bg-surface-light" />
                  <div className="h-2.5 w-1/2 rounded bg-surface-light" />
                </div>
              </div>
              <div className="flex gap-1.5 mb-3">
                {Array.from({ length: 4 }).map((_, j) => (
                  <div key={j} className="w-6 h-6 rounded-full bg-surface-light" />
                ))}
              </div>
              <div className="h-7 rounded-lg bg-surface-light" />
            </div>
          ))}
        </div>
      ) : brands.length ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {brands.map((b) => (
            <div key={b.brand_ref} className="group rounded-2xl border border-glass-border bg-surface-light/40 p-4 hover:border-primary/30 transition-colors">
              <div className="flex items-center gap-3 mb-3">
                <span className="w-12 h-12 rounded-xl overflow-hidden bg-surface-light flex items-center justify-center shrink-0">
                  {b.logo_url
                    ? <img src={b.logo_url} alt="" className="w-full h-full object-cover" />
                    : <BookMarked className="w-5 h-5 text-text-tertiary" />}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-text-primary truncate">{b.name}</p>
                  <p className="text-xs text-text-tertiary capitalize">{b.motion_style || t('assets.motionStyleDefault')} · {[b.title_font, b.body_font].filter(Boolean).join(' / ') || t('assets.defaultFonts')}</p>
                </div>
              </div>
              {/* palette swatches */}
              <div className="flex gap-1.5 mb-3">
                {[b.primary_color, b.secondary_color, b.tertiary_color, b.accent_color].map((c, i) =>
                  c ? <span key={i} className="w-6 h-6 rounded-full border border-white/15" style={{ backgroundColor: c }} title={c} /> : null
                )}
              </div>
              {b.brand_identity && (
                <p className="text-xs text-text-tertiary leading-relaxed line-clamp-2 mb-3">{b.brand_identity}</p>
              )}
              <div className="flex items-center gap-2">
                <button onClick={() => useBrand(b)} className="flex-1 py-1.5 rounded-lg bg-gradient-to-r from-primary to-accent text-white text-[11px] font-bold flex items-center justify-center gap-1">
                  <Sparkles className="w-3 h-3" /> {t('assets.useInVideo')}
                </button>
                <button onClick={() => setModal({ open: true, existing: b })} title={t('assets.edit')}
                  className="w-7 h-7 rounded-full hover:bg-surface-light flex items-center justify-center text-text-tertiary hover:text-text-primary">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => void removeBrand(b)} title={t('assets.delete')}
                  className="w-7 h-7 rounded-full hover:bg-surface-light flex items-center justify-center text-text-tertiary hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : !note && (
        <div className="rounded-2xl border border-dashed border-glass-border p-10 text-center">
          <BookMarked className="w-8 h-8 text-text-tertiary mx-auto mb-3" />
          <p className="text-sm font-semibold text-text-primary mb-1">{t('assets.noBrandKitsYet')}</p>
          <p className="text-xs text-text-tertiary max-w-sm mx-auto mb-4">
            {t('assets.brandKitsEmptyDesc')}
          </p>
          <button
            onClick={() => setModal({ open: true })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary to-accent text-white text-xs font-semibold"
          >
            <BookMarked className="w-3.5 h-3.5" /> {t('assets.createFirstBrandKit')}
          </button>
        </div>
      )}

      {modal.open && (
        <BrandKitModal
          existing={modal.existing}
          onClose={() => setModal({ open: false })}
          onSaved={() => { void load(); toast.success(modal.existing ? t('assets.brandKitUpdated') : t('assets.brandKitCreated')); }}
        />
      )}

    </section>
  );
}

// ---------------------------------------------------------------------------
// Files — honest state (file library is a later phase; no fake data)
// ---------------------------------------------------------------------------

function FilesSection() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const [assets, setAssets] = useState<V2Asset[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [uploadPct, setUploadPct] = useState<number | null>(null);
  const [busyRef, setBusyRef] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await v2Service.listAssets(userId);
      setAssets(res.assets || []);
      setNote(null);
    } catch {
      setNote(NOT_DEPLOYED());
    }
  }, [userId]);

  useEffect(() => { void load(); }, [load]);

  // While any file is still processing on HeyGen, poll the library so it flips to
  // 'ready' on its own (the list endpoint self-heals processing rows). Pauses on
  // a hidden tab; stops once nothing is processing.
  const anyProcessing = assets.some((a) => a.status === 'processing');
  useEffect(() => {
    if (!anyProcessing) return;
    const id = setInterval(() => { if (!document.hidden) void load(); }, 5000);
    return () => clearInterval(id);
  }, [anyProcessing, load]);

  const upload = async (files: FileList | null) => {
    if (!files?.length || !userId) return;
    let uploaded = 0;
    for (const file of Array.from(files)) {
      const bad = assetFileError(file);
      if (bad) {
        toast.error(bad.reason === 'unsupported'
          ? t('assets.unsupportedType', { name: file.name, types: SUPPORTED_ASSET_TYPES })
          : t('assets.fileTooLargeMax', { name: file.name, max: bad.maxMb }));
        continue;
      }
      try {
        setUploadPct(1);
        await v2Service.uploadAsset(userId, file, (p) => setUploadPct(p));
        trackAmpAssetUploaded({ source: 'assets_page', fileType: file.type, sizeBucket: assetSizeBucket(file.size) });
        await load();
        uploaded += 1;
      } catch {
        toast.error(t('assets.uploadFailed', { name: file.name }));
      } finally {
        setUploadPct(null);
      }
    }
    if (uploaded > 0) toast.success(uploaded === 1 ? t('assets.fileUploaded') : t('assets.filesUploaded', { count: uploaded }));
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = async (a: V2Asset) => {
    if (!userId || busyRef) return;
    if (!await vConfirm(t('assets.deleteFileConfirm', { name: a.name }))) return;
    setBusyRef(a.asset_ref);
    try { await v2Service.deleteAsset(userId, a.asset_ref); trackAmpAssetDeleted({ assetType: 'file' }); await load(); toast.success(t('assets.itemDeleted', { name: a.name })); }
    catch { toast.error(t('assets.deleteFailed')); }
    finally { setBusyRef(null); }
  };

  const useInVideo = (a: V2Asset) => {
    seedHomeAgent({ fileAssetId: a.asset_id, fileName: a.name, filePreviewUrl: a.asset_type === 'image' ? a.url || undefined : undefined, fileType: a.asset_type || a.content_type });
    navigate('/');
  };

  const sizeLabel = (n?: number) => {
    if (!n) return '';
    if (n > 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(n / 1024))} KB`;
  };

  return (
    <section className="mb-12">
      <h2 className="text-xl font-bold text-text-primary mb-5">{t('assets.files')}</h2>
      <input ref={fileRef} type="file" multiple accept="image/*,video/*,.pdf" className="hidden" onChange={(e) => void upload(e.target.files)} />
      <AddNewPills
        options={[{
          icon: Upload,
          label: uploadPct !== null ? t('assets.uploadingPct', { pct: uploadPct }) : t('assets.uploadFiles'),
          onClick: () => { if (uploadPct === null) fileRef.current?.click(); },
        }]}
      />
      <Note text={note} />

      {assets.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {assets.map((a) => (
            <div key={a.asset_ref} className="group">
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-surface-light mb-2 group-hover:ring-2 group-hover:ring-primary/40 transition-all">
                {a.asset_type === 'image' && a.url
                  ? <img src={a.url} alt={a.name} loading="lazy" className="w-full h-full object-cover" />
                  : a.asset_type === 'video' && a.url
                    ? <video src={a.url} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-text-tertiary">
                        {a.asset_type === 'audio' ? <AudioLines className="w-6 h-6" />
                          : a.asset_type === 'document' ? <FileText className="w-6 h-6 text-rose-300" />
                          : a.asset_type === 'video' ? <Video className="w-6 h-6" />
                          : <FolderOpen className="w-6 h-6" />}
                        <span className="text-[10px] uppercase font-bold">{a.asset_type === 'document' ? 'PDF' : (a.asset_type || t('assets.fileTypeFallback'))}</span>
                      </div>
                    )}
                {a.status === 'uploading' && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-amber-500/80 text-black text-[10px] font-bold">{t('assets.incomplete')}</span>
                )}
                {a.status === 'processing' && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-primary/85 text-white text-[10px] font-bold flex items-center gap-1">
                    <Loader2 className="w-2.5 h-2.5 animate-spin" /> {t('assets.processing')}
                  </span>
                )}
                <div className="absolute inset-x-2 bottom-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {a.status === 'ready' && (
                    <button onClick={() => useInVideo(a)} className="flex-1 py-1.5 rounded-lg bg-gradient-to-r from-primary to-accent text-white text-[11px] font-bold flex items-center justify-center gap-1">
                      <Sparkles className="w-3 h-3" /> {t('assets.useInVideo')}
                    </button>
                  )}
                  <button onClick={() => void remove(a)} disabled={busyRef === a.asset_ref}
                    className="px-2.5 py-1.5 rounded-lg bg-black/70 text-white/90 hover:text-red-300 text-[11px] font-bold disabled:opacity-50">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <p className="text-sm font-medium text-text-primary truncate">{a.name}</p>
              <p className="text-xs text-text-tertiary">{[a.asset_type, sizeLabel(a.size_bytes)].filter(Boolean).join(' · ')}</p>
            </div>
          ))}
        </div>
      ) : !note && (
        <div className="rounded-2xl border border-dashed border-glass-border p-10 text-center">
          <FolderOpen className="w-8 h-8 text-text-tertiary mx-auto mb-3" />
          <p className="text-sm font-semibold text-text-primary mb-1">{t('assets.noFilesYet')}</p>
          <p className="text-xs text-text-tertiary max-w-sm mx-auto mb-4">
            {t('assets.filesEmptyDesc')}
          </p>
          <button
            onClick={() => fileRef.current?.click()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary to-accent text-white text-xs font-semibold"
          >
            <Upload className="w-3.5 h-3.5" /> {t('assets.uploadFirstFile')}
          </button>
        </div>
      )}
    </section>
  );
}

export function AssetsPage() {
  const { t } = useTranslation('v2');
  const [activeTab, setActiveTab] = useState<TabId>('all');

  return (
    <div className="max-w-5xl mx-auto px-8 py-10">
      <h1 className="text-3xl font-extrabold text-text-primary mb-8">{t('assets.title')}</h1>

      {/* Tabs */}
      <div className="flex gap-2 mb-10 overflow-x-auto scrollbar-hide">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={cn(
              'px-5 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors',
              activeTab === tab.id
                ? 'bg-gradient-to-r from-primary to-accent text-white'
                : 'glass text-text-secondary hover:text-text-primary'
            )}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {(activeTab === 'all' || activeTab === 'avatars') && <AvatarsSection />}
      {(activeTab === 'all' || activeTab === 'voices') && <VoicesSection />}
      {(activeTab === 'all' || activeTab === 'styles') && <BrandsSection />}
      {(activeTab === 'all' || activeTab === 'files') && <FilesSection />}
    </div>
  );
}
